import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { Moon, Coffee, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { useApi, errMsg, spanMinutes, fmtHours, toMin } from "../lib/api";

const Preview = ({ form, settings }) => {
  if (!form.start_time || !form.end_time || form.start_time === form.end_time) return null;
  const span = spanMinutes(form.start_time, form.end_time);
  const brk = form.manual_break
    ? Number(form.break_minutes || 0)
    : settings?.auto_break_enabled && span > settings.auto_break_after_hours * 60 ? settings.auto_break_minutes : 0;
  const overnight = toMin(form.end_time) <= toMin(form.start_time);
  return (
    <div data-testid="shift-preview" className="grid grid-cols-3 gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div><div className="flex items-center gap-1 text-[11px] uppercase text-muted-foreground"><Clock className="h-3 w-3" />Total</div><div className="font-num font-bold">{fmtHours(span)}</div></div>
      <div><div className="flex items-center gap-1 text-[11px] uppercase text-muted-foreground"><Coffee className="h-3 w-3" />Break</div><div className="font-num font-bold">{brk}m</div></div>
      <div><div className="text-[11px] uppercase text-muted-foreground">Paid</div><div data-testid="shift-preview-paid" className="font-num font-bold text-amber-400">{fmtHours(Math.max(0, span - brk))}</div></div>
      {overnight && <div className="col-span-3 flex items-center gap-1.5 text-xs text-indigo-300"><Moon className="h-3.5 w-3.5" />Ends the next day (night shift)</div>}
    </div>
  );
};

export default function ShiftEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    date: params.get("date") || format(new Date(), "yyyy-MM-dd"),
    start_time: "08:00", end_time: "16:30", manual_break: false, break_minutes: 30, note: "",
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api("get", "/settings") });
  const { data: existing } = useQuery({ queryKey: ["shift", id], queryFn: () => api("get", `/shifts/${id}`), enabled: !!id });

  useEffect(() => {
    if (existing) setForm({ ...existing, manual_break: existing.break_minutes !== null, break_minutes: existing.break_minutes ?? 30 });
  }, [existing]);

  const save = useMutation({
    mutationFn: () => {
      const body = { date: form.date, start_time: form.start_time, end_time: form.end_time, note: form.note,
        break_minutes: form.manual_break ? Number(form.break_minutes || 0) : null };
      return id ? api("put", `/shifts/${id}`, body) : api("post", "/shifts", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["shift", id] });
      toast.success(id ? "Shift updated" : "Shift added");
      navigate(id && window.history.state?.idx > 0 ? -1 : "/dashboard");
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="shift-editor-page" className="mx-auto max-w-xl space-y-8">
      <div>
        <p className="font-num text-xs font-bold uppercase tracking-wider text-amber-500">{id ? "Edit" : "New"} shift</p>
        <h1 className="mt-1 font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">{id ? "Edit Shift" : "Add Shift"}</h1>
      </div>
      <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="space-y-2">
          <Label htmlFor="date">Date</Label>
          <Input id="date" type="date" required value={form.date} onChange={set("date")} data-testid="shift-date-input" className="h-12 bg-card" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="start">Start</Label>
            <Input id="start" type="time" required value={form.start_time} onChange={set("start_time")} data-testid="shift-start-input" className="h-12 bg-card font-num text-lg" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="end">End</Label>
            <Input id="end" type="time" required value={form.end_time} onChange={set("end_time")} data-testid="shift-end-input" className="h-12 bg-card font-num text-lg" />
          </div>
        </div>
        <div className="space-y-3 rounded-2xl border border-border/70 bg-card p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="manual-break">Enter break manually</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {settings ? (settings.auto_break_enabled ? `Otherwise: ${settings.auto_break_minutes} min for shifts over ${settings.auto_break_after_hours}h` : "Auto break is off in settings") : ""}
              </p>
            </div>
            <Switch id="manual-break" checked={form.manual_break} data-testid="shift-manual-break-switch"
              onCheckedChange={(v) => setForm((f) => ({ ...f, manual_break: v }))} />
          </div>
          {form.manual_break && (
            <div className="space-y-2">
              <Label htmlFor="break">Break (minutes)</Label>
              <Input id="break" type="number" min="0" max="720" value={form.break_minutes} onChange={set("break_minutes")} data-testid="shift-break-input" className="h-11 bg-background font-num" />
            </div>
          )}
        </div>
        <Preview form={form} settings={settings} />
        <div className="space-y-2">
          <Label htmlFor="note">Note (optional)</Label>
          <Textarea id="note" maxLength={300} value={form.note} onChange={set("note")} placeholder="e.g. Route 42, covered for Sam" data-testid="shift-note-input" className="bg-card" />
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} data-testid="shift-cancel-btn"
            className="flex-1 rounded-full border border-border py-3.5 font-semibold hover:bg-secondary">Cancel</button>
          <button type="submit" disabled={save.isPending} data-testid="shift-save-btn"
            className="flex-[2] rounded-full bg-amber-500 py-3.5 font-bold text-[#0B0F17] transition-transform hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60">
            {save.isPending ? "Saving…" : id ? "Save changes" : "Save shift"}
          </button>
        </div>
      </form>
    </div>
  );
}
